import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useRouter } from '~/utils/navigation';
import { H2 } from '@comeon/ui';
import { isBiggerSelector } from '../../../layout/mediaquery';
import useTranslations from '../../../translation/use-translations';
import useIntervalAction from '../../../../hooks/use-interval-action.js';
import { fetchSessions, FETCH_SESSIONS } from '../ducks/we-spin-duck.js';
import {
	selectSessionsLoaded,
	selectSession,
	selectDefaultEventForSession,
	selectIsSessionOnline,
	selectConfiguration
} from '../ducks/we-spin-selectors.js';
import {
	ROUTE_WE_SPIN_GAME_ROOM_ABS,
	ROUTE_WE_SPIN_GAME_ROOM_INFO,
	ROUTE_WE_SPIN_GAME_ROOM_EVENT_LIST,
	ROUTE_WE_SPIN_GAME_ROOM_CHAT
} from '../constants/we-spin-route-constants.js';
import { ENDED, BEFORE_RUNNING_STATES, RUNNING } from '../constants/we-spin-stream-states.js';
import StreamElement from './stream-element.js';
import { WE_SPIN_GAME_VIEW_TEXT_KEYS } from '../constants/we-spin-text-keys.js';
import StartTime from '../components/start-time.js';
import { sidebarTop } from '../../../../utils/layout-utils.js';
import useUIContext from '../../../../ui/use-ui-context';
import useContextualPathTransformer from '../../../../hooks/use-contextual-path-transformer.js';
import useDecodeContextLocation from '../../../../hooks/use-decode-context-location.js';
import useContextForLocation from '../../../../hooks/use-context-for-location.js';
import { updateMainContentLoading } from '~/components/brand/ducks/index.js';

const Dimmer = ({ streamView, overlayImage, isSessionOnline }) => {
	const { translations: tr, isLoadingTranslations } = useTranslations(WE_SPIN_GAME_VIEW_TEXT_KEYS);
	return (
		!isLoadingTranslations && (
			<div
				className="u-relative u-width-100"
				style={{
					backgroundImage: overlayImage && `url(${overlayImage})`,
					backgroundSize: 'cover',
					backgroundRepeat: 'no-repeat',
					backgroundPosition: 'center'
				}}
			>
				<div className="u-text-c u-relative u-width-100 u-height-100 u-center-vertically u-bg-overlay-70">
					{BEFORE_RUNNING_STATES.includes(streamView.state) ? (
						<H2>
							<StartTime state={streamView.state} start={streamView.start} joined={streamView.joined} />
						</H2>
					) : streamView.state === ENDED ? (
						<>
							<H2>{tr.WE_SPIN_GAME_VIEW_ENDED_TITLE}</H2>
							<H2>{tr.WE_SPIN_GAME_VIEW_ENDED_TEXT}</H2>
						</>
					) : !isSessionOnline ? (
						<H2>{tr.WE_SPIN_GAME_VIEW_OFFLINE_TITLE}</H2>
					) : (
						<H2>{tr.WE_SPIN_GAME_VIEW_CANCELLED_TITLE}</H2>
					)}
				</div>
			</div>
		)
	);
};

const getSize = (hasHeader, hasProductNav, hasStaticHeader) => {
	const offset = sidebarTop({ hasHeader, hasProductNav, hasStaticHeader });
	return {
		small: {
			height: '200px',
			width: '375px'
		},
		large: {
			style: { height: `calc(100vh - ${offset})` },
			width: undefined
		}
	};
};

const WeSpinGameView = () => {
	const router = useRouter();
	const { ref } = useParams();
	const isBigger = useSelector(isBiggerSelector);
	const streamViewsLoaded = useSelector(selectSessionsLoaded);
	const [isOpeningSidebarForSession, setIsOpeningSidebarForSession] = useState('');
	const { hasHeader, hasSubHeader, hasStaticHeader } = useUIContext();

	const { weSpinViewListPollInterval } = useSelector(selectConfiguration);
	useIntervalAction(FETCH_SESSIONS, fetchSessions(), weSpinViewListPollInterval || 5000);

	const session = useSelector(selectSession(ref));
	const isSessionOnline = useSelector(selectIsSessionOnline(ref));

	const streamView = useSelector(selectDefaultEventForSession(ref));
	const dispatch = useDispatch();
	const { state = '', joined } = streamView || {};

	// Is WeSpin Game Room in a contex (e.g. in the sidebar)
	const context = useContextForLocation(ROUTE_WE_SPIN_GAME_ROOM_ABS);
	const contextLocation = useDecodeContextLocation(context);
	const transformPath = useContextualPathTransformer();
	useEffect(() => {
		if (
			!contextLocation?.pathname ||
			(!contextLocation.pathname.startsWith(ROUTE_WE_SPIN_GAME_ROOM_ABS) && isOpeningSidebarForSession !== ref)
		) {
			let link = '';
			if (BEFORE_RUNNING_STATES.includes(state)) {
				link = `${ROUTE_WE_SPIN_GAME_ROOM_INFO}/${ref}`;
			} else {
				link = `${
					state === RUNNING ? ROUTE_WE_SPIN_GAME_ROOM_CHAT : ROUTE_WE_SPIN_GAME_ROOM_EVENT_LIST
				}/${ref}`;
			}

			router.replace(transformPath(link));
		}
		setIsOpeningSidebarForSession(ref);
	}, [router, isOpeningSidebarForSession, contextLocation.pathname, ref, state, joined, transformPath]);

	useEffect(() => {
		dispatch(updateMainContentLoading('loaded'));
	}, [dispatch]);
	if (!streamViewsLoaded || !streamView) return null;
	const { streamUrl, streamerBgUrl } = session;
	const showDimmer = !isSessionOnline;
	return (
		isBigger &&
		streamView && (
			<div
				className="game-view u-pl1 game-view--we-spin"
				{...getSize(hasHeader, hasSubHeader, hasStaticHeader).large}
			>
				<div className="game-view__wrapper u-flex u-oh u-height-100">
					{showDimmer && (
						<Dimmer
							streamView={streamView}
							overlayImage={streamerBgUrl}
							isSessionOnline={isSessionOnline}
						/>
					)}
					<div className="game-view__iframe u-flex-1">
						<StreamElement
							size="large"
							type={isSessionOnline ? 'STREAM' : 'IMAGE'}
							streamLinkUrl={isSessionOnline ? streamUrl : streamerBgUrl}
							header={hasHeader}
							productnav={hasSubHeader}
							regulations={hasStaticHeader}
							position="absolute"
						/>
					</div>
				</div>
			</div>
		)
	);
};

export default WeSpinGameView;
